import { useState, useRef } from 'react'
import { opportunitiesService } from '@/services/opportunities-service'

interface Props {
  onClose: () => void
  onCreated: (opp: any) => void
}

type TabType = 'pdf' | 'url' | 'manual'

export function OpportunitySubmitModal({ onClose, onCreated }: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('pdf')
  const [loading, setLoading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // PDF Tab state
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pdfTitle, setPdfTitle] = useState('')

  // URL Tab state
  const [urlInput, setUrlInput] = useState('')
  const [urlTitle, setUrlTitle] = useState('')

  // Manual Entry state
  const [manualData, setManualData] = useState({
    title: '',
    description: '',
    scopeOfWork: '',
    projectAddress: '',
    projectTypes: [] as string[],
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    estimatedBudget: '',
    dueDate: '',
  })

  const projectTypeOptions = [
    { label: 'Asphalt', value: 'asphalt' },
    { label: 'Concrete', value: 'concrete' },
    { label: 'Grading', value: 'grading' },
    { label: 'Seal Coat', value: 'seal-coat' },
    { label: 'Striping', value: 'striping' },
  ]

  // PDF Tab handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      const file = files[0]
      if (file.type === 'application/pdf') {
        setPdfFile(file)
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPdfFile(e.target.files[0])
    }
  }

  const handleUploadPdf = async () => {
    if (!pdfFile) return

    try {
      setLoading(true)
      const result = await opportunitiesService.uploadPdf(pdfFile, pdfTitle || undefined)
      onCreated(result)
      onClose()
    } catch (err) {
      console.error('Failed to upload PDF:', err)
    } finally {
      setLoading(false)
    }
  }

  // URL Tab handlers
  const handleFetchUrl = async () => {
    if (!urlInput.trim()) return

    try {
      setLoading(true)
      const result = await opportunitiesService.fromUrl(urlInput, urlTitle || undefined)
      onCreated(result)
      onClose()
    } catch (err) {
      console.error('Failed to fetch URL:', err)
    } finally {
      setLoading(false)
    }
  }

  // Manual Entry handlers
  const handleManualChange = (field: keyof typeof manualData, value: any) => {
    setManualData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleProjectTypeToggle = (type: string) => {
    setManualData(prev => ({
      ...prev,
      projectTypes: prev.projectTypes.includes(type)
        ? prev.projectTypes.filter(t => t !== type)
        : [...prev.projectTypes, type]
    }))
  }

  const handleCreateManual = async () => {
    if (!manualData.title.trim()) return

    try {
      setLoading(true)
      const payload = {
        ...manualData,
        estimatedBudget: manualData.estimatedBudget ? parseFloat(manualData.estimatedBudget) : undefined,
        projectTypes: manualData.projectTypes,
      }
      const result = await opportunitiesService.createManual(payload)
      onCreated(result)
      onClose()
    } catch (err) {
      console.error('Failed to create opportunity:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="opp-modal-overlay">
      <div className="opp-modal-card">
        <button className="opp-modal-close" onClick={onClose} disabled={loading}>
          ✕
        </button>

        <h2 className="opp-modal-title">New Opportunity</h2>

        {/* Tab buttons */}
        <div className="opp-tab-bar">
          <button
            className={`opp-tab-btn ${activeTab === 'pdf' ? 'opp-tab-btn-active' : ''}`}
            onClick={() => setActiveTab('pdf')}
            disabled={loading}
          >
            Upload PDF
          </button>
          <button
            className={`opp-tab-btn ${activeTab === 'url' ? 'opp-tab-btn-active' : ''}`}
            onClick={() => setActiveTab('url')}
            disabled={loading}
          >
            From URL
          </button>
          <button
            className={`opp-tab-btn ${activeTab === 'manual' ? 'opp-tab-btn-active' : ''}`}
            onClick={() => setActiveTab('manual')}
            disabled={loading}
          >
            Manual Entry
          </button>
        </div>

        {/* Tab content */}
        <div className="opp-tab-content">
          {/* Upload PDF Tab */}
          {activeTab === 'pdf' && (
            <div>
              <div
                className={`opp-dropzone ${dragActive ? 'opp-dropzone-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <svg className="opp-dropzone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className="opp-dropzone-text">Drag and drop your PDF here, or click to browse</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="opp-modal-btn-secondary"
                  disabled={loading}
                >
                  Browse Files
                </button>
              </div>

              {pdfFile && (
                <div className="opp-form-group">
                  <p style={{ fontSize: '0.875rem', color: 'var(--s600)' }}>
                    Selected: {pdfFile.name}
                  </p>
                </div>
              )}

              <div className="opp-form-group">
                <label className="opp-form-label">Title (optional)</label>
                <input
                  type="text"
                  className="opp-form-input"
                  placeholder="e.g., Main Street Paving Project"
                  value={pdfTitle}
                  onChange={e => setPdfTitle(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-modal-actions">
                <button
                  className="opp-modal-btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="opp-modal-btn-primary"
                  onClick={handleUploadPdf}
                  disabled={!pdfFile || loading}
                >
                  {loading ? 'Uploading...' : 'Upload & Analyze'}
                </button>
              </div>
            </div>
          )}

          {/* From URL Tab */}
          {activeTab === 'url' && (
            <div>
              <div className="opp-form-group">
                <label className="opp-form-label">URL *</label>
                <input
                  type="url"
                  className="opp-form-input"
                  placeholder="https://..."
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-form-group">
                <label className="opp-form-label">Title (optional)</label>
                <input
                  type="text"
                  className="opp-form-input"
                  placeholder="e.g., City Project Listing"
                  value={urlTitle}
                  onChange={e => setUrlTitle(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-modal-actions">
                <button
                  className="opp-modal-btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="opp-modal-btn-primary"
                  onClick={handleFetchUrl}
                  disabled={!urlInput.trim() || loading}
                >
                  {loading ? 'Fetching...' : 'Fetch & Analyze'}
                </button>
              </div>
            </div>
          )}

          {/* Manual Entry Tab */}
          {activeTab === 'manual' && (
            <div>
              <div className="opp-form-group">
                <label className="opp-form-label">Title *</label>
                <input
                  type="text"
                  className="opp-form-input"
                  placeholder="e.g., Downtown Parking Lot Reseal"
                  value={manualData.title}
                  onChange={e => handleManualChange('title', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-form-group">
                <label className="opp-form-label">Description</label>
                <textarea
                  className="opp-form-textarea"
                  placeholder="Project details, notes, etc."
                  rows={2}
                  value={manualData.description}
                  onChange={e => handleManualChange('description', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-form-group">
                <label className="opp-form-label">Scope of Work</label>
                <textarea
                  className="opp-form-textarea"
                  placeholder="Describe the work to be performed: mobilization, milling, paving, striping, etc."
                  rows={4}
                  value={manualData.scopeOfWork}
                  onChange={e => handleManualChange('scopeOfWork', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-form-group">
                <label className="opp-form-label">Project Address</label>
                <input
                  type="text"
                  className="opp-form-input"
                  placeholder="123 Main St, Springfield, IL 62701"
                  value={manualData.projectAddress}
                  onChange={e => handleManualChange('projectAddress', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-form-group">
                <label className="opp-form-label">Project Types</label>
                <div className="opp-checkbox-group">
                  {projectTypeOptions.map(opt => (
                    <label key={opt.value} className="opp-checkbox-item">
                      <input
                        type="checkbox"
                        checked={manualData.projectTypes.includes(opt.value)}
                        onChange={() => handleProjectTypeToggle(opt.value)}
                        disabled={loading}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="opp-form-row">
                <div className="opp-form-group" style={{ flex: 1 }}>
                  <label className="opp-form-label">Contact Name</label>
                  <input
                    type="text"
                    className="opp-form-input"
                    placeholder="John Smith"
                    value={manualData.contactName}
                    onChange={e => handleManualChange('contactName', e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="opp-form-group" style={{ flex: 1 }}>
                  <label className="opp-form-label">Contact Email</label>
                  <input
                    type="email"
                    className="opp-form-input"
                    placeholder="john@example.com"
                    value={manualData.contactEmail}
                    onChange={e => handleManualChange('contactEmail', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="opp-form-row">
                <div className="opp-form-group" style={{ flex: 1 }}>
                  <label className="opp-form-label">Contact Phone</label>
                  <input
                    type="tel"
                    className="opp-form-input"
                    placeholder="(555) 123-4567"
                    value={manualData.contactPhone}
                    onChange={e => handleManualChange('contactPhone', e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="opp-form-group" style={{ flex: 1 }}>
                  <label className="opp-form-label">Estimated Budget</label>
                  <input
                    type="number"
                    className="opp-form-input"
                    placeholder="50000"
                    value={manualData.estimatedBudget}
                    onChange={e => handleManualChange('estimatedBudget', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="opp-form-group">
                <label className="opp-form-label">Due Date</label>
                <input
                  type="date"
                  className="opp-form-input"
                  value={manualData.dueDate}
                  onChange={e => handleManualChange('dueDate', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="opp-modal-actions">
                <button
                  className="opp-modal-btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="opp-modal-btn-primary"
                  onClick={handleCreateManual}
                  disabled={!manualData.title.trim() || loading}
                >
                  {loading ? 'Creating...' : 'Create Opportunity'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
